import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppPushParamFieldChange,
    applyAppPushDraft,
    applyAppPushTargetSelection,
    clearAppPushDraft,
    createAppPushSubmitFormData,
    createDefaultAppPushParams,
    createAppPushDraftPayload,
    fetchAppPushListData,
    getAppPushCoupangSearchState,
    getAppPushSubmitErrorMessage,
    NEW_APP_PUSH_DRAFT_KEY,
    readAppPushDraft,
    saveAppPushDraft,
    toggleAppPushWeekType,
} from './appPushDraftUtils'
import AppPushFormFields from './AppPushFormFields'
import { AppPushCoupangTargetTable, AppPushMeetTargetTable } from './AppPushTargetTables'
import { getLegacyTimeTypeFromReserveTime } from './pushReserveTime'

export default function NewAppPush() {
    const location = useLocation()
    const history = useHistory()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || { searchStatus: 99, searchPopupStatus: '' }
    const listSearch = location.search || ''
    const draftKey = NEW_APP_PUSH_DRAFT_KEY
    const loading = false
    const [saving, setSaving] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [meetWeTotalCount, setMeetWeTotalCount] = useState(0)
    const [coupangTotalCount, setCoupangTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(1)
    const [meetWeCurPage, setMeetWeCurPage] = useState(1)
    const [coupangCurPage, setCoupangCurPage] = useState(1)
    const offset = 5
    const [animalDatas, setAnimalDatas] = useState([])
    const [meetWeDatas, setMeetWeDatas] = useState([])
    const [coupangDatas, setCoupangDatas] = useState([])
    const [params, setParams] = useState(() => createDefaultAppPushParams())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [searchText, setSearchText] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchSelect] = useState({
        keyword1: '',
    })
    
    const [checked, setChecked] = useState([])
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftStateRef = useRef(null)
    const initialDraftSnapshotRef = useRef('')

    if (!initialDraftStateRef.current) {
        initialDraftStateRef.current = {
            params,
            checked,
            curPage,
            meetWeCurPage,
            coupangCurPage,
            searchText,
            searchQuery,
        }
        initialDraftSnapshotRef.current = JSON.stringify(createAppPushDraftPayload({
            params,
            checked,
            curPage,
            meetWeCurPage,
            coupangCurPage,
            searchText,
            searchQuery,
        }))
    }

    useEffect(() => {
        const storedDraft = readAppPushDraft(draftKey)
        const initialDraftState = initialDraftStateRef.current

        if (storedDraft && initialDraftState) {
            const restoredDraft = applyAppPushDraft({
                baseParams: initialDraftState.params,
                baseChecked: initialDraftState.checked,
                baseCurPage: initialDraftState.curPage,
                baseMeetWeCurPage: initialDraftState.meetWeCurPage,
                baseCoupangCurPage: initialDraftState.coupangCurPage,
                baseSearchText: initialDraftState.searchText,
                baseSearchQuery: initialDraftState.searchQuery,
                draft: storedDraft,
            })

            setParams(restoredDraft.params)
            setChecked(restoredDraft.checked)
            setCurPage(restoredDraft.curPage)
            setMeetWeCurPage(restoredDraft.meetWeCurPage)
            setCoupangCurPage(restoredDraft.coupangCurPage)
            setSearchText(restoredDraft.searchText)
            setSearchQuery(restoredDraft.searchQuery)
        }

        setDraftReady(true)
    }, [draftKey])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // App 푸시 등록은 목록 query 대신 sessionStorage로 draft를 이어간다.
        const nextDraft = createAppPushDraftPayload({
            params,
            checked,
            curPage,
            meetWeCurPage,
            coupangCurPage,
            searchText,
            searchQuery,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppPushDraft(draftKey)
            return
        }

        saveAppPushDraft(draftKey, nextDraft)
    }, [checked, coupangCurPage, curPage, draftKey, draftReady, meetWeCurPage, params, searchQuery, searchText])

    const showListLoadError = useCallback((message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar('error')
        setMessageSnackbar(message)
    }, [])

    const getAnimalData = useCallback(async () => {
        await fetchAppPushListData({
            request: (payload) => API.post('/api/v1/admin/appPushHistory/getAnimalList', payload),
            payload: { page: curPage, offset },
            setTotalCount,
            setRows: setAnimalDatas,
            onForbidden: () => history.push('/login'),
            onError: showListLoadError,
            fallbackErrorMessage: '애니멀런 목록을 불러오지 못했습니다.',
        })
    }, [curPage, history, offset, showListLoadError])

    const getMeetWeData = useCallback(async () => {
        await fetchAppPushListData({
            request: (payload) => API.post('/api/v1/admin/appPushHistory/getMeetWeList', payload),
            payload: { page: meetWeCurPage, offset },
            setTotalCount: setMeetWeTotalCount,
            setRows: setMeetWeDatas,
            onForbidden: () => history.push('/login'),
            onError: showListLoadError,
            fallbackErrorMessage: '우리끼리 대회 목록을 불러오지 못했습니다.',
        })
    }, [history, meetWeCurPage, offset, showListLoadError])

    const getCoupangData = useCallback(async () => {
        await fetchAppPushListData({
            request: (payload) => API.post('/api/v1/admin/appPushHistory/getCoupangList', payload),
            payload: {
                page: coupangCurPage,
                offset,
                searchText: searchQuery,
                searchSelect,
            },
            setTotalCount: setCoupangTotalCount,
            setRows: setCoupangDatas,
            onForbidden: () => history.push('/login'),
            onError: showListLoadError,
            fallbackErrorMessage: '쿠팡 상품 목록을 불러오지 못했습니다.',
        })
    }, [coupangCurPage, history, offset, searchQuery, searchSelect, showListLoadError])

    useEffect(() => {
        if (Number(params.pushType) !== 2) return
        getAnimalData()
    }, [getAnimalData, params.pushType])
    useEffect(() => {
        if (Number(params.pushType) !== 4) return
        getMeetWeData()
    }, [getMeetWeData, params.pushType])
    useEffect(() => {
        if (Number(params.pushType) !== 3) return
        getCoupangData()
    }, [getCoupangData, params.pushType])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleUpdateField = (e) => {
        if (e.target.name === 'pushType') {
            setCurPage(1)
            setMeetWeCurPage(1)
            setCoupangCurPage(1)
        }

        setParams(applyAppPushParamFieldChange({
            params,
            name: e.target.name,
            value: e.target.value,
            pushTypeExtraResets: {
                3: {
                    meetAnimalId: null,
                },
            },
        }))
    }

    const handleReserveTimeChange = (name) => (e) => {
        setParams({
            ...params,
            [name]: e.target.value,
        })
    }

    const createData = async (params) => {
        try {
            setSaving(true)
            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appPushHistory/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 등록 후에도 이전 App 푸시 목록 조건으로 그대로 복귀시킨다.
                clearAppPushDraft(draftKey)
                history.push(createListLinkTarget({
                    pathname: `/appPushs`,
                    search: listSearch,
                    openSnackbar: true,
                    severitySnackbar: 'success',
                    messageSnackbar: data.message,
                    curPage: listCurPage,
                    searchText: listSearchText,
                    searchSelect: listSearchSelect
                }))
                return
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 푸시를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const submitErrorMessage = getAppPushSubmitErrorMessage({
            params,
            checked,
        })

        if (submitErrorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(submitErrorMessage)
            return false
        }

        const formData = createAppPushSubmitFormData({
            params,
            checked,
            getLegacyTimeTypeFromReserveTime,
        })

        createData(formData)
    }

    const chooseButtonClick = (meetId) => {
        setParams(applyAppPushTargetSelection({
            params,
            field: 'meetId',
            value: meetId,
        }))
    }

    const chooseButtonClick2 = (coupangId) => {
        setParams(applyAppPushTargetSelection({
            params,
            field: 'coupangId',
            value: coupangId,
        }))
    }

    const handleChange = (e) => {
        setChecked(toggleAppPushWeekType({
            checked,
            value: e.target.value,
            isChecked: e.target.checked,
        }))
    }

    const searchButtonClick = () => {
        const { shouldFetchCurrentPage, shouldResetPage, nextSearchQuery } = getAppPushCoupangSearchState({
            currentPage: coupangCurPage,
            searchQuery,
            searchText,
        })

        if (shouldFetchCurrentPage) {
            getCoupangData()
            return
        }

        setSearchQuery(nextSearchQuery)

        if (shouldResetPage) {
            setCoupangCurPage(1)
        }
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }
    
    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>APP 푸시 발송 등록</Title>
                {/* 애니멀런 상품 리스트 */}
                {params.pushType === 2 &&
                <AppPushMeetTargetTable
                    rows={animalDatas}
                    totalCount={totalCount}
                    curPage={curPage}
                    offset={offset}
                    setCurPage={setCurPage}
                    selectedMeetId={params.meetId}
                    imageHeader="상품이미지"
                    titleHeader="상품명"
                    rowKey={(row) => row.meetAnimalId}
                    imageSrc={(row) => `${process.env.REACT_APP_AWS_S3_URL}/${row.thumbnail}`}
                    onSelect={(row) => chooseButtonClick(row.meetId)}
                    saving={saving}
                />
                }
                {/* 우리끼리 대회 리스트 */}
                {params.pushType === 4 &&
                <AppPushMeetTargetTable
                    rows={meetWeDatas}
                    totalCount={meetWeTotalCount}
                    curPage={meetWeCurPage}
                    offset={offset}
                    setCurPage={setMeetWeCurPage}
                    selectedMeetId={params.meetId}
                    imageHeader="이미지"
                    titleHeader="대회명"
                    rowKey={(row) => row.meetId}
                    imageSrc={(row) => `${process.env.REACT_APP_AWS_S3_URL}/${row.thumbnail}`}
                    onSelect={(row) => chooseButtonClick(row.meetId)}
                    saving={saving}
                />
                }
                {/* 쿠팡 상품 리스트 */}
                { params.pushType === 3 &&
                <AppPushCoupangTargetTable
                    rows={coupangDatas}
                    captionTotalCount={totalCount}
                    totalCount={coupangTotalCount}
                    curPage={coupangCurPage}
                    offset={offset}
                    setCurPage={setCoupangCurPage}
                    selectedCoupangId={params.coupangId}
                    searchText={searchText}
                    onSearchTextChange={(e) => setSearchText(e.target.value)}
                    onSearchSubmit={(e) => {
                        e.preventDefault()
                        searchButtonClick()
                    }}
                    onSelect={(row) => chooseButtonClick2(row.coupangId)}
                    saving={saving}
                />
                }
                <AppPushFormFields
                    params={params}
                    checked={checked}
                    saving={saving}
                    onSubmit={handleSubmit}
                    onFieldChange={handleUpdateField}
                    onReserveHourChange={handleReserveTimeChange('reserveHour')}
                    onReserveMinuteChange={handleReserveTimeChange('reserveMinute')}
                    onWeekTypeChange={handleChange}
                    onStartTimeChange={(date) => setParams({ ...params, startTime: date })}
                    onEndTimeChange={(date) => setParams({ ...params, endTime: date })}
                    listSearch={listSearch}
                    listCurPage={listCurPage}
                    listSearchText={listSearchText}
                    listSearchSelect={listSearchSelect}
                    onBackClick={() => clearAppPushDraft(draftKey)}
                    submitLabel={saving ? '저장 중...' : '저장'}
                />
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
